import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Globe, Mail, Lock, ArrowRight, Loader2, Eye, EyeOff, Building2, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrgAuthPage() {
  const [mode, setMode] = useState("login");
  const [orgName, setOrgName] = useState("");
  const [email, setEmail] = useState(() => localStorage.getItem("org_email") || "");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(!!localStorage.getItem("org_email"));
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const pwMatch = password && passwordConfirm && password === passwordConfirm;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/org/login`, { email, password });
      localStorage.setItem("org_token", res.data.token);
      localStorage.setItem("org_user", JSON.stringify(res.data.user));
      // Clear company token to avoid conflicts
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (remember) localStorage.setItem("org_email", email);
      else localStorage.removeItem("org_email");
      toast.success("Giriş başarılı!");
      navigate("/organizator-panel");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Giriş başarısız");
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirm) { toast.error("Şifreler eşleşmiyor"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/org/register`, { email, password, org_name: orgName });
      localStorage.setItem("org_token", res.data.token);
      localStorage.setItem("org_user", JSON.stringify(res.data.user));
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      toast.success("Kayıt başarılı!");
      navigate("/organizator-panel");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-8" data-testid="org-auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#1B4F72] flex items-center justify-center mx-auto mb-4 rounded-sm">
            <Globe className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            {mode === "login" ? "Organizatör Girişi" : "Organizatör Kaydı"}
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            {mode === "login" ? "Organizatör panelinize erişin" : "Fuar organizasyonunuzun profilini oluşturun — ücretsiz"}
          </p>
        </div>

        <form onSubmit={mode === "login" ? handleLogin : handleRegister} className="bg-white border border-stone-200 p-6 space-y-4">
          {mode === "register" && (
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Organizasyon Adı</Label>
              <p className="text-[10px] text-[#1B4F72] mb-1">Kuruluş/şirket adı (kişi adı değil)</p>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} required placeholder="ör: Tüyap Fuarcılık" className="pl-10 rounded-none border-stone-300" data-testid="org-name" />
              </div>
            </div>
          )}
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">E-posta</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="info@organizasyon.com" className="pl-10 rounded-none border-stone-300" data-testid="org-email" />
            </div>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Şifre</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input value={password} onChange={(e) => setPassword(e.target.value)} type={showPw ? "text" : "password"} required minLength={6} placeholder="••••••" className="pl-10 pr-10 rounded-none border-stone-300" data-testid="org-password" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          {mode === "register" && (
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Şifre Tekrar</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} type={showPw ? "text" : "password"} required placeholder="Tekrar" className={`pl-10 rounded-none ${passwordConfirm && !pwMatch ? "border-red-400" : pwMatch ? "border-green-400" : "border-stone-300"}`} />
                {pwMatch && <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />}
              </div>
            </div>
          )}
          {mode === "login" && (
            <div className="flex items-center gap-2">
              <Checkbox id="org-remember" checked={remember} onCheckedChange={(c) => setRemember(!!c)} className="rounded-sm" />
              <label htmlFor="org-remember" className="text-xs text-stone-500 cursor-pointer">Beni hatırla</label>
            </div>
          )}
          <Button type="submit" disabled={loading || (mode === "register" && !pwMatch)} className="w-full rounded-none bg-[#1B4F72] hover:bg-[#154360] font-mono text-xs uppercase tracking-widest h-11" data-testid="org-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{mode === "login" ? "Giriş Yap" : "Kayıt Ol"} <ArrowRight className="w-3.5 h-3.5 ml-2" /></>}
          </Button>
        </form>
        <p className="text-center text-sm text-stone-500 mt-4">
          {mode === "login" ? (
            <>Hesabınız yok mu? <button onClick={() => setMode("register")} className="text-[#1B4F72] hover:underline font-medium">Kayıt Olun</button></>
          ) : (
            <>Hesabınız var mı? <button onClick={() => setMode("login")} className="text-[#1B4F72] hover:underline font-medium">Giriş Yapın</button></>
          )}
        </p>
      </div>
    </div>
  );
}
